import { Alert, Button, Group, Modal, ScrollArea, Stack, Table, Text } from '@mantine/core';
import { FileCsv, Warning } from '@phosphor-icons/react';
import { type FC, type ReactNode, useRef, useState } from 'react';

import { parseDelimited } from '../vendorReceive/rows';

import { emptyLine, type LineDraft, type SkuOption, textColors } from './shared';

/**
 * Bulk-add line items from a CSV instead of one row at a time. Entirely
 * client-side: the file is parsed in the browser and each row's SKU is
 * resolved against the already-loaded catalog — matched rows become line
 * drafts, unmatched ones are listed with the reason and never added
 * (same contract as the picker's "Add this SKU to your catalog first").
 */

const TEMPLATE_CSV = 'sku,quantity,unit_price\nTSBPK12CGE22001,100,1499\n';

/** Header aliases, matched case-insensitively with spaces/underscores stripped. */
const HEADER_KEYS: Record<string, 'sku' | 'quantity' | 'unitPrice' | 'description'> = {
    sku: 'sku',
    // The shipment form keys its rows by product id rather than SKU; same column, other names.
    productid: 'sku',
    itemid: 'sku',
    quantity: 'quantity',
    qty: 'quantity',
    shipment: 'quantity',
    shipmentquantity: 'quantity',
    shipmentqty: 'quantity',
    unitprice: 'unitPrice',
    price: 'unitPrice',
    rate: 'unitPrice',
    description: 'description',
    item: 'description',
};

type ParsedRow = { row: number; draft?: LineDraft; reason?: string; sku: string };

function parseLineItemsCsv(
    text: string,
    skuOptions: SkuOption[],
    unmatchedReason = 'not in catalog',
): { rows: ParsedRow[]; headerError?: string } {
    const cells = parseDelimited(text, ',');
    if (cells.length < 2) return { rows: [], headerError: 'The file has no data rows.' };

    const normalize = (h: string) => h.toLowerCase().replace(/[\s_-]/g, '');
    const colOf: Partial<Record<'sku' | 'quantity' | 'unitPrice' | 'description', number>> = {};
    cells[0].forEach((h, i) => {
        const key = HEADER_KEYS[normalize(h)];
        if (key && colOf[key] === undefined) colOf[key] = i;
    });
    if (colOf.sku === undefined || colOf.quantity === undefined) {
        return { rows: [], headerError: 'Header row must include "sku" and "quantity" columns.' };
    }

    const bySku = new Map(skuOptions.map((o) => [o.sku.toLowerCase(), o]));
    const rows: ParsedRow[] = cells.slice(1).map((r, i) => {
        const rowNum = i + 2;
        const sku = (r[colOf.sku!] ?? '').trim();
        if (!sku) return { row: rowNum, sku, reason: 'blank SKU' };
        const found = bySku.get(sku.toLowerCase());
        if (!found) return { row: rowNum, sku, reason: unmatchedReason };
        const quantity = Number(r[colOf.quantity!]);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            return { row: rowNum, sku, reason: `invalid quantity "${r[colOf.quantity!] ?? ''}"` };
        }
        const rawPrice = colOf.unitPrice === undefined ? '' : (r[colOf.unitPrice] ?? '').trim();
        const unitPrice = rawPrice === '' ? '' : Number(rawPrice.replace(/,/g, ''));
        if (unitPrice !== '' && (!Number.isFinite(unitPrice) || unitPrice < 0)) {
            return { row: rowNum, sku, reason: `invalid unit price "${rawPrice}"` };
        }
        const description = colOf.description === undefined ? '' : (r[colOf.description] ?? '').trim();
        return {
            row: rowNum,
            sku: found.sku,
            draft: {
                ...emptyLine(),
                sku: found.sku,
                description: description || found.name,
                quantity,
                unitPrice,
                materialId: found.kind === 'material' ? found.id : null,
                productId: found.kind === 'product' ? found.id : null,
            },
        };
    });
    return { rows };
}

export const LineItemsCsvModal: FC<{
    opened: boolean;
    onClose: () => void;
    skuOptions: SkuOption[];
    /** Why a SKU outside `skuOptions` is skipped. Defaults to "not in catalog". */
    unmatchedReason?: string;
    onImport: (lines: LineDraft[]) => void;
    /**
     * Wording for a caller whose rows are not catalogue lines — the shipment form uses this
     * same modal to fill quantities against an order's lines, keyed by product id.
     */
    title?: string;
    intro?: ReactNode;
    templateCsv?: string;
    templateFileName?: string;
    confirmLabel?: (count: number) => string;
}> = ({
    opened,
    onClose,
    skuOptions,
    unmatchedReason,
    onImport,
    title = 'Add line items from CSV',
    intro,
    templateCsv = TEMPLATE_CSV,
    templateFileName = 'line-items-template.csv',
    confirmLabel,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
    const [headerError, setHeaderError] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string>('');

    const reset = () => {
        setParsed(null);
        setHeaderError(null);
        setFileName('');
        if (inputRef.current) inputRef.current.value = '';
    };
    const handleClose = () => {
        reset();
        onClose();
    };

    const handleFile = async (file: File | undefined) => {
        if (!file) return;
        setFileName(file.name);
        const { rows, headerError: err } = parseLineItemsCsv(await file.text(), skuOptions, unmatchedReason);
        setHeaderError(err ?? null);
        setParsed(err ? null : rows);
    };

    const ready = (parsed ?? []).filter((r) => r.draft);
    const skipped = (parsed ?? []).filter((r) => !r.draft);

    const downloadTemplate = () => {
        const url = URL.createObjectURL(new Blob([templateCsv], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = templateFileName;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={title}
            size="lg"
            zIndex={3000}
        >
            <Stack gap={12}>
                <Text size="13.5px" c={textColors.secondary} lh={1.6}>
                    {intro ?? (
                        <>
                            Columns: <b>sku</b>, <b>quantity</b>, optional <b>unit_price</b> and{' '}
                            <b>description</b>. SKUs must already exist in your catalog — anything
                            unrecognised is listed below and skipped.
                        </>
                    )}{' '}
                    <Text
                        component="span"
                        size="13.5px"
                        fw={500}
                        c="rgb(1, 119, 204)"
                        style={{ cursor: 'pointer' }}
                        onClick={downloadTemplate}
                    >
                        Download template
                    </Text>
                </Text>

                <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={(e) => void handleFile(e.target.files?.[0])}
                />

                {headerError ? (
                    <Alert color="red" icon={<Warning size={16} />} p={10}>
                        <Text size="12px">{headerError}</Text>
                    </Alert>
                ) : null}

                {parsed ? (
                    <>
                        <Text size="12px" fw={600} c={textColors.primary}>
                            {ready.length} row{ready.length === 1 ? '' : 's'} ready to add
                            {skipped.length ? ` · ${skipped.length} skipped` : ''}
                        </Text>
                        {skipped.length ? (
                            <ScrollArea.Autosize mah={140}>
                                <Table withRowBorders={false} verticalSpacing={2}>
                                    <Table.Tbody>
                                        {skipped.map((r) => (
                                            <Table.Tr key={r.row}>
                                                <Table.Td w={60}>
                                                    <Text size="11px" c={textColors.muted}>
                                                        row {r.row}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="11px" c={textColors.primary}>
                                                        {r.sku || '—'}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="11px" c="red">
                                                        {r.reason}
                                                    </Text>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea.Autosize>
                        ) : null}
                    </>
                ) : null}

                <Group justify="space-between" gap={8} mt={4}>
                    <Group gap={8}>
                        <Button
                            size="xs"
                            variant="light"
                            color="rgb(1, 149, 255)"
                            leftSection={<FileCsv size={14} />}
                            onClick={() => inputRef.current?.click()}
                        >
                            {fileName ? 'Choose a different file' : 'Choose CSV file'}
                        </Button>
                        {fileName ? (
                            <Text size="12px" c={textColors.secondary}>
                                {fileName}
                            </Text>
                        ) : null}
                    </Group>
                    <Group gap={8}>
                        <Button size="xs" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            size="xs"
                            disabled={ready.length === 0}
                            onClick={() => {
                                onImport(ready.map((r) => r.draft!));
                                handleClose();
                            }}
                        >
                            {confirmLabel
                                ? confirmLabel(ready.length)
                                : `Add ${ready.length || ''} item${ready.length === 1 ? '' : 's'}`}
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
};
